import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/translation.css';
import { Link } from 'react-router-dom';

const TranslationDashboard = () => {
  const [translations, setTranslations] = useState({ en: {}, no: {} });
  const [translatableContent, setTranslatableContent] = useState({});
  const [selectedTable, setSelectedTable] = useState('');
  const [editValues, setEditValues] = useState({});
  const { user } = useAuth();

  useEffect(() => {
    fetchTranslations();
    fetchTranslatableContent();
  }, []);

  const fetchTranslations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/translations`);
      setTranslations(response.data);
      setEditValues(response.data);
    } catch (error) {
      console.error('Error fetching translations:', error);
    }
  };

  const fetchTranslatableContent = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/translations/content`,
        { withCredentials: true }
      );
      setTranslatableContent(response.data);
    } catch (error) {
      console.error('Error fetching translatable content:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const updates = [];
      Object.entries(editValues).forEach(([language, translations]) => {
        Object.entries(translations).forEach(([key, value]) => {
          if (key.startsWith(selectedTable)) {
            updates.push({ key, language, value });
          }
        });
      });

      for (const update of updates) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/translations/update`,
          update,
          { withCredentials: true }
        );
      }

      fetchTranslations();
      alert('Oversettelser oppdatert!');
    } catch (error) {
      console.error('Error updating translations:', error);
      alert('Feil ved oppdatering av oversettelser');
    }
  };

  const handleInitialize = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/translations/initialize`,
        {},
        { withCredentials: true }
      );
      fetchTranslations();
    } catch (error) {
      console.error('Error initializing translations:', error);
    }
  };

  const handleInputChange = (key, language, value) => {
    setEditValues(prev => ({
      ...prev,
      [language]: {
        ...prev[language],
        [key]: value
      }
    }));
  };

  const isTextArea = (field) => {
    return field.includes('description');
  };

  if (!user || user.role !== 'superadmin') {
    return <div>Tilgang nektet. Kun superadmin.</div>;
  }

  const renderTranslationFields = (row) => {
    const fields = Object.entries(row).filter(([key]) => !key.startsWith('id'));
    const idField = Object.keys(row).find(key => key.startsWith('id'));

    return fields.map(([field, value]) => {
      const translationKey = `${selectedTable}_${row[idField]}_${field}`;
      return (
        <tr key={translationKey}>
          <td className="field-name">{field}</td>
          <td>
            {isTextArea(field) ? (
              <textarea
                value={editValues.no?.[translationKey] || value}
                onChange={(e) => handleInputChange(translationKey, 'no', e.target.value)}
                className="translation-textarea"
                placeholder="Norsk oversettelse"
              />
            ) : (
              <input
                type="text"
                value={editValues.no?.[translationKey] || value}
                onChange={(e) => handleInputChange(translationKey, 'no', e.target.value)}
                className="translation-input"
                placeholder="Norsk oversettelse"
              />
            )}
          </td>
          <td>
            {isTextArea(field) ? (
              <textarea
                value={editValues.en?.[translationKey] || ''}
                onChange={(e) => handleInputChange(translationKey, 'en', e.target.value)}
                className="translation-textarea"
                placeholder="Engelsk oversettelse"
              />
            ) : (
              <input
                type="text"
                value={editValues.en?.[translationKey] || ''}
                onChange={(e) => handleInputChange(translationKey, 'en', e.target.value)}
                className="translation-input"
                placeholder="Engelsk oversettelse"
              />
            )}
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="translation-dashboard">
      <div className="translation-header">
      <Link to="/dashboard" className="back-button">
          <i className="fas fa-arrow-left"></i> Tilbake til Dashboard
        </Link>
        <h1>Oversettelser</h1>
        <button
          onClick={handleInitialize}
          className="initialize-button"
        >
          Initialiser oversettelser
        </button>
      </div>

      <div className="translation-tables">
        <h2>Tilgjengelige tabeller</h2>
        <div className="table-buttons">
          {Object.keys(translatableContent).map(table => (
            <button
              key={table}
              onClick={() => setSelectedTable(table)}
              className={`table-button ${selectedTable === table ? 'active' : ''}`}
            >
              {table}
            </button>
          ))}
        </div>
      </div>

      {selectedTable && translatableContent[selectedTable] && (
        <div className="translation-content">
          <div className="content-header">
            <h2>Tabellinnhold: {selectedTable}</h2>
            <button
              onClick={handleSubmit}
              className="save-button"
            >
              Lagre alle endringer
            </button>
          </div>
          
          <div className="overflow-x-auto">
            {translatableContent[selectedTable].map((row, rowIndex) => (
              <div key={rowIndex} className="mb-8">
                <div className="item-header">
                  <h3>ID: {row[Object.keys(row).find(key => key.startsWith('id'))]}</h3>
                </div>
                <table className="translation-table">
                  <thead>
                    <tr>
                      <th>Felt</th>
                      <th>Norsk</th>
                      <th>Engelsk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTranslationFields(row)}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationDashboard; 